import {get, isEmpty, isNil} from 'lodash'
import uuid from 'helpers/uuid'
import {getNumberFromLetterSequence} from 'helpers/string'
import {canUseCosts, getMinSupplierQuantity, getPackTypeName, PartType} from 'helpers/parts'
import {createItemDescription, getTotalQuantityForItem} from 'helpers/orders'
import {getDefaultPack, getPartPacks} from 'helpers/packs'
import {getPartSuppliers} from 'helpers/partSuppliers'
import {convertToCurrency} from "helpers/financial"

export const getPurchaseOrderedQuantityForOrderItem = (orderItem) => {
  const purchaseOrderItems = get(orderItem, 'purchaseOrderItems', []) || []
  return purchaseOrderItems.reduce((total, item) => total + item.quantity, 0)
}

export const getDefaultPackForPurchaseOrder = (part) => {
  if (part && part.packs && part.packs.length > 0) {
    const pack = part.packs.find(pack => pack.default)
    return pack || part.packs[0]
  }

  return null
}

export const createPurchaseOrderItem = (props) => {
  const {
    purchaseOrder,
    part = null,
    packTypes,
    defaults,
    purchasingUseManufacturerCode,
  } = props

  let taxCode
  let taxCodeId
  let taxRate

  if (part) {
    taxCode = get(part, 'taxCode', null)
    taxCodeId = get(part, 'taxCode.id', null)
    taxRate = get(part, 'taxCode.rate', null)
  }

  if (isNil(taxCodeId)) {
    taxCode = get(purchaseOrder, 'taxCode')
    taxCodeId = get(purchaseOrder, 'taxCodeId')
    taxRate = get(purchaseOrder, 'taxRate')
  }

  const currencyRate = get(purchaseOrder, 'currencyRate', 1)
  const pack = getDefaultPackForPurchaseOrder(part)
  const partCode = getPartCode(part, purchasingUseManufacturerCode)

  const ret = {
    isNew: true,
    description: createItemDescription({part}),
    discount: 0,
    nominalCode: get(part, 'purchaseNominalCode', null),
    departmentName: get(part, 'purchaseDepartmentName', ''),
    notes: get(part, 'notes', ''),
    purchaseOrder: null,
    purchaseOrderId: null,
    part,
    partCode,
    partId: part ? part.id : null,
    partPackDesc: pack ? getPackTypeName(pack.packTypeId, packTypes) : 'Unit(s)',
    partPackId: pack ? pack.id : null,
    msrp: pack ? pack.msrp : null,
    partType: part ? part.type : PartType.NON_STOCK.id,
    quantity: getMinOrderQuantity(part, get(purchaseOrder, 'companyId'), 1),
    taxCode,
    taxRate,
    taxCodeId,
    unitCost: 0,
    totalCost: 0,
    tax: 0,
    netTax: 0,
    netCost: 0,
    currencyRate,
    supplierDiscount: 0,
    ...defaults,
  }

  if (part) {
    if (part.sizeX) ret.length = part.sizeX
    if (part.sizeY) ret.width = part.sizeY
    if (part.sizeZ) ret.height = part.sizeZ
    if (!isNil(part.lengthClass)) ret.lengthClass = part.lengthClass
    if (!isNil(part.weight)) ret.weight = part.weight
    if (!isNil(part.weightClass)) ret.weightClass = part.weightClass
  }

  return ret
}

export const getPurchaseOrderItemPartCode = (orderItem, isPurchasingUseManufacturerCodeEnabled) => {
  const part = get(orderItem, 'part', null)
  const partCode = get(orderItem, 'part.partCode', null)
  const manCode = get(orderItem, 'part.manCode', null)
  const itemPartCode = get(orderItem, 'partCode', null)

  if (isNil(part))
    return itemPartCode || 'ITEM'

  if (!isPurchasingUseManufacturerCodeEnabled)
    return partCode

  if (isEmpty(manCode))
    return partCode || itemPartCode

  return manCode
}

export const createPurchaseOrderItemFromOrderItem = (
    orderItem,
    order,
    defaultNominalCode,
    allItems,
    isPurchasingUseManufacturerCodeEnabled = false,
    purchaseOrder) => {

  const convertedCost = convertToCurrency(orderItem.unitCost, purchaseOrder.currencyRate)

  return {
    id: uuid(),
    orderId: order.id,
    orderItemId: orderItem.id,
    partId: orderItem.partId,
    partCode: getPurchaseOrderItemPartCode(orderItem, isPurchasingUseManufacturerCodeEnabled),
    description: orderItem.desc,
    quantity: getTotalQuantityForItem(orderItem, allItems),
    partPackId: orderItem.partPackId,
    partPackDesc: orderItem.partPackDesc,
    partType: orderItem.partType,
    unitCost: orderItem.partId ? null : convertedCost,
    length: orderItem.length,
    width: orderItem.width,
    height: orderItem.height,
    nominalCode: get(orderItem, 'part.purchaseNominalCode', null) || defaultNominalCode,
    notes: orderItem.notes,
    quantityReceived: 0,
    quantityReturned: 0,
    order,
  }
}

export const updateOrderItem = (item) => {
  const totalCost = item.unitCost * item.quantity
  const totalTax = totalCost * item.taxRate

  return {
    ...item,
    totalCost,
    totalTax,
  }
}

export const copyOrderItem = (item) => {
  return {
    ...item,
    id: uuid(),
    guid: uuid(),
    isNew: true,
  }
}

export const addItems = (currentItems, newItems) => {
  return [
    ...currentItems,
    ...newItems.map((item, index) => {
      const itemId = currentItems.length + index + 1
      return {
        ...item,
        id: -1,
        guid: uuid(),
        itemId,
        lineItemLetter: getNumberFromLetterSequence(itemId),
      }
    }),
  ]
}

export const filterDeletedItems = (items, deletedItems) => {
  return items.filter(i => !deletedItems.includes(i.id))
}

export const purchaseOrderItemHasDimension = (item, key) => {
  if (isNil(get(item, 'part', null))) {
    return false
  }

  return item.part.hasOwnProperty(key)
}

export const getPartFieldsForPurchaseOrderItem = (purchaseOrder, part, packTypes) => {
  const purchaseOrderItem = {
    part: part,
    partId: get(part, 'id', null),
    partCode: get(part, 'partCode', null),
    description: createItemDescription({part}),
    partType: get(part, 'type', null),
    length: get(part, 'sizeX', null),
    width: get(part, 'sizeY', null),
    height: get(part, 'sizeZ', null),
    nominalCode: get(part, 'purchaseNominalCode', null),
    taxCodeId: get(part, 'taxCode.id', null),
    taxRate: get(part, 'taxCode.rate', null),
    notes: get(part, 'notes', ''),
    ...getPartPackFieldsForPurchaseOrderItem(part, packTypes),
  }

  if (isNil(purchaseOrderItem.taxCodeId))
    purchaseOrderItem.taxCodeId = get(purchaseOrder, 'taxCodeId', null)

  if (isNil(purchaseOrderItem.taxRate))
    purchaseOrderItem.taxRate = get(purchaseOrder, 'taxRate', null)

  return purchaseOrderItem
}

export const getMinOrderQuantity = (part, supplierId, defaultIfNone = null) => {
  const partPacks = getPartPacks(part, true)
  const defaultPack = getDefaultPack(partPacks)
  const partSuppliers = getPartSuppliers(defaultPack)
  const minOrderQuantity = partSuppliers
          ?.find(supplier => get(supplier, 'companyId') === supplierId)?.minQuantity
      ?? defaultIfNone

  const supplier = isEmpty(partSuppliers) ? null : partSuppliers[0]
  return !isNil(minOrderQuantity) && minOrderQuantity > 1
      ? minOrderQuantity
      : canUseCosts(supplier) ? getMinSupplierQuantity(supplier)
          : 1
}

export const getPartPackFieldsForPurchaseOrderItem = (part, packTypes) => {
  const partPacks = getPartPacks(part, true)
  const defaultPack = getDefaultPack(partPacks)
  return defaultPack ? {
    partPackId: defaultPack.id,
    partPackDesc: getPackTypeName(defaultPack.packTypeId, packTypes),
  } : {}
}

export const getPartCode = (part, purchasingUseManufacturerCode) => {
  if (isNil(part))
    return 'ITEM'

  const partCode = get(part, 'partCode', null)
  const manCode = get(part, 'manCode', null)

  if (!purchasingUseManufacturerCode)
    return partCode

  return isEmpty(manCode)
      ? partCode
      : manCode
}

export const getOutstandingQuantity = (purchaseOrderItem) => {
  return Math.max(
      0,
      purchaseOrderItem.quantity - purchaseOrderItem.quantityReceived + purchaseOrderItem.quantityReturned)
}

export const getMaxReturnableQuantity = (purchaseOrderItem, quantityReceiving = 0) => {
  return purchaseOrderItem.quantityReceived - purchaseOrderItem.quantityReturned + quantityReceiving
}

export const isItemFullyDelivered = (purchaseOrderItem) => {
  return purchaseOrderItem.quantityReceived - purchaseOrderItem.quantityReturned >= purchaseOrderItem.quantity
}
